import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { hostAPI, bookingsAPI } from '../services/api';
import { 
  ChevronLeft, 
  ChevronRight, 
  Home, 
  Plus, 
  Calendar as CalendarIcon,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  Lock,
  Unlock,
  Eye,
  Users,
  Phone,
  Mail,
  MapPin,
  AlertTriangle,
  CreditCard
} from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import HostPanVerification from '../components/HostPanVerification';
import axios from 'axios';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImageUrl = (imagePath) => {
  if (!imagePath) return 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400';
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
  if (imagePath.startsWith('/api/')) return `${BACKEND_URL}${imagePath}`;
  if (imagePath.startsWith('/uploads/')) return `${BACKEND_URL}/api${imagePath}`;
  return imagePath;
};

const HostDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hostData, setHostData] = useState(null);
  const [properties, setProperties] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedRoomId, setSelectedRoomId] = useState('all');
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [blockedDates, setBlockedDates] = useState([]);
  const [bookedDates, setBookedDates] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [hostBookings, setHostBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [cancellations, setCancellations] = useState([]);
  const [cancellationsLoading, setCancellationsLoading] = useState(false);
  const [respondingId, setRespondingId] = useState(null);
  const [respondAction, setRespondAction] = useState(null); // 'accept' | 'reject'
  const [hostMessage, setHostMessage] = useState('');
  const [responding, setResponding] = useState(false);
  const [panVerified, setPanVerified] = useState(false);

  const hostId = localStorage.getItem('hostId');

  const fetchHostBookings = useCallback(async () => {
    if (!hostId) return;
    try {
      setBookingsLoading(true);
      const response = await bookingsAPI.getByHost(hostId);
      if (response.success) {
        setHostBookings(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching host bookings:', error);
    } finally {
      setBookingsLoading(false);
    }
  }, [hostId]);

  const fetchCancellations = useCallback(async () => {
    if (!hostId) return;
    try {
      setCancellationsLoading(true);
      const response = await bookingsAPI.getHostCancellations(hostId);
      if (response.success) {
        setCancellations(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching cancellations:', error);
    } finally {
      setCancellationsLoading(false);
    }
  }, [hostId]);

  const fetchDashboard = useCallback(async () => {
    if (!hostId) return;
    try {
      setLoading(true);
      const response = await hostAPI.getDashboard(hostId);
      if (response.success) {
        setHostData(response.host);
        setProperties(response.properties);
        setStats(response.stats);
        
        // Auto-select first approved property
        const approvedProperty = response.properties.find(p => p.status === 'approved');
        if (approvedProperty) {
          setSelectedProperty(approvedProperty);
        }

        // Fetch bookings
        fetchHostBookings();
        fetchCancellations();
      }

      // Fetch PAN verification status separately (non-blocking)
      try {
        const vRes = await axios.get(`${BACKEND_URL}/api/host/verification-status/${hostId}`);
        if (vRes.data?.success) setPanVerified(!!vRes.data.data?.verified);
      } catch (e) {
        // Non-blocking — leave panVerified=false on error
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [hostId, fetchHostBookings, fetchCancellations]);

  useEffect(() => {
    if (!hostId) {
      toast.error('Please login as a host first');
      navigate('/host/register');
      return;
    }
    fetchDashboard();
  }, [hostId, navigate, fetchDashboard]);

  const handleCancellationResponse = async (cancellationId, action) => {
    try {
      setResponding(true);
      const response = await bookingsAPI.respondCancellation({
        cancellation_id: cancellationId,
        action,
        host_message: hostMessage,
        host_id: hostId
      });
      if (response.success) {
        toast.success(action === 'accept'
          ? 'Cancellation accepted — calendar freed and guest notified'
          : 'Cancellation rejected — guest notified');
        setRespondingId(null);
        setRespondAction(null);
        setHostMessage('');
        fetchCancellations();
        fetchHostBookings();
        fetchAvailability();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond to cancellation');
    } finally {
      setResponding(false);
    }
  };

  const fetchAvailability = useCallback(async () => {
    if (!selectedProperty) return;
    
    try {
      setCalendarLoading(true);
      const response = await hostAPI.getAvailability(
        selectedProperty.id,
        String(calendarMonth + 1),
        String(calendarYear)
      );
      if (response.success) {
        setBlockedDates(response.blocked_dates || []);
        setBookedDates(response.booked_dates || []);
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
    } finally {
      setCalendarLoading(false);
    }
  }, [selectedProperty, calendarMonth, calendarYear]);

  useEffect(() => {
    fetchAvailability();
    setSelectedDates([]); // Clear selection when month changes
  }, [fetchAvailability]);

  useEffect(() => {
    // Reset selected room when property changes
    setSelectedRoomId('all');
  }, [selectedProperty?.id]);

  const handleBlockDates = async () => {
    if (selectedDates.length === 0) {
      toast.error('Please select dates to block');
      return;
    }

    try {
      setActionLoading(true);
      const response = await hostAPI.blockDates(hostId, selectedProperty.id, selectedDates, 'blocked');
      if (response.success) {
        toast.success(`Blocked ${selectedDates.length} date(s)`);
        setSelectedDates([]);
        fetchAvailability();
      }
    } catch (error) {
      toast.error('Failed to block dates');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnblockDates = async () => {
    if (selectedDates.length === 0) {
      toast.error('Please select dates to unblock');
      return;
    }

    try {
      setActionLoading(true);
      const response = await hostAPI.unblockDates(hostId, selectedProperty.id, selectedDates);
      if (response.success) {
        toast.success(`Unblocked ${response.unblocked_count} date(s)`);
        setSelectedDates([]);
        fetchAvailability();
      }
    } catch (error) {
      toast.error('Failed to unblock dates');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleDateSelection = (dateStr) => {
    // Don't allow selecting booked dates
    if (bookedDates.includes(dateStr)) {
      toast.error('Cannot modify booked dates');
      return;
    }
    
    setSelectedDates(prev => 
      prev.includes(dateStr) 
        ? prev.filter(d => d !== dateStr)
        : [...prev, dateStr]
    );
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const formatDateStr = (day) => {
    const month = String(calendarMonth + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    return `${calendarYear}-${month}-${dayStr}`;
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(calendarMonth, calendarYear);
    const firstDay = getFirstDayOfMonth(calendarMonth, calendarYear);
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    // For per-room properties: derive bookedDates from confirmed host bookings filtered by selected room
    const isPerRoom = selectedProperty?.listing_type === 'rooms';
    let effectiveBookedDates = bookedDates;
    if (isPerRoom && selectedRoomId !== 'all') {
      const perRoomDates = new Set();
      (hostBookings || []).forEach((b) => {
        if (b.property_id !== selectedProperty.id && b.property_id !== selectedProperty._id) return;
        // Match this booking against the selected room
        const matches = b.room_id === selectedRoomId
          || (b.room_selections || []).some((s) => s.room_id === selectedRoomId);
        if (!matches) return;
        // Add each night between check_in and check_out
        if (b.check_in && b.check_out) {
          let d = new Date(b.check_in);
          const end = new Date(b.check_out);
          while (d < end) {
            const ds = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            perRoomDates.add(ds);
            d = new Date(d.getTime() + 24 * 3600 * 1000);
          }
        }
      });
      effectiveBookedDates = Array.from(perRoomDates);
    }

    const days = [];
    
    // Empty cells for days before the first day of month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-10 sm:h-12" />);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = formatDateStr(day);
      const isBlocked = blockedDates.includes(dateStr);
      const isBooked = effectiveBookedDates.includes(dateStr);
      const isSelected = selectedDates.includes(dateStr);
      const isPast = dateStr < todayStr;
      const isToday = dateStr === todayStr;
      
      let bgColor = 'bg-white hover:bg-gray-100';
      let textColor = 'text-gray-900';
      let cursor = 'cursor-pointer';
      
      if (isPast) {
        bgColor = 'bg-gray-50';
        textColor = 'text-gray-400';
        cursor = 'cursor-not-allowed';
      } else if (isBooked) {
        bgColor = 'bg-red-100';
        textColor = 'text-red-700';
        cursor = 'cursor-not-allowed';
      } else if (isBlocked) {
        bgColor = 'bg-gray-300';
        textColor = 'text-gray-600';
      } else if (isSelected) {
        bgColor = 'bg-[#7CB342]';
        textColor = 'text-white';
      }
      
      days.push(
        <div
          key={day}
          onClick={() => !isPast && toggleDateSelection(dateStr)}
          className={`h-10 sm:h-12 flex items-center justify-center rounded-lg border transition-all ${bgColor} ${textColor} ${cursor} ${isToday ? 'ring-2 ring-[#7CB342]' : ''} ${isSelected ? 'ring-2 ring-offset-2 ring-[#7CB342]' : ''}`}
          data-testid={`calendar-day-${day}`}
        >
          <div className="text-center">
            <span className="text-sm font-medium">{day}</span>
            {isBooked && <div className="w-1.5 h-1.5 bg-red-500 rounded-full mx-auto mt-0.5" />}
            {isBlocked && !isBooked && <div className="w-1.5 h-1.5 bg-gray-500 rounded-full mx-auto mt-0.5" />}
          </div>
        </div>
      );
    }
    
    return days;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="h-3 w-3 mr-1" /> Approved</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#7CB342] mx-auto" />
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/')} className="mb-4">
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to home
          </Button>
        </div>

        {/* PAN Verification (KYC) — prominent at top */}
        <div className="mb-6" data-testid="host-pan-status-widget">
          <HostPanVerification
            hostId={hostId}
            hostName={hostData?.name}
            onVerifiedChange={setPanVerified}
          />
        </div>

        {/* Welcome row */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Host Dashboard</h1>
              <p className="text-gray-600 mt-1">Welcome back, {hostData?.name}</p>
            </div>
            <Button 
              onClick={() => panVerified ? navigate('/list-property') : toast.error('Please verify your PAN first to add a property')}
              disabled={!panVerified}
              className="bg-[#7CB342] hover:bg-[#6da036] text-white disabled:opacity-60"
              title={!panVerified ? 'Verify your PAN first' : ''}
              data-testid="host-add-property-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Property
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Properties</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Home className="h-8 w-8 text-[#7CB342]" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Approved</p>
                  <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Rejected</p>
                  <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="col-span-2 md:col-span-1 bg-gradient-to-br from-[#7CB342]/10 to-white border-[#7CB342]/30" data-testid="host-net-earnings-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Net Earnings</p>
                  <p className="text-2xl font-bold text-[#7CB342]">
                    ₹{(() => {
                      const confirmed = (hostBookings || []).filter(b => b.booking_status === 'confirmed' || b.payment_status === 'paid' || b.payment_status === 'completed');
                      const total = confirmed.reduce((sum, b) => {
                        const sub = b.subtotal ?? b.total_amount ?? 0;
                        const fee = b.platform_fee ?? Math.round(sub * 0.10 * 100) / 100;
                        return sum + (sub - fee);
                      }, 0);
                      return Math.round(total).toLocaleString();
                    })()}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">after platform fee</p>
                </div>
                <CreditCard className="h-8 w-8 text-[#7CB342]" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Properties List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">My Properties</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[600px] overflow-y-auto">
                {properties.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Home className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No properties yet</p>
                    <Button 
                      variant="link" 
                      onClick={() => panVerified ? navigate('/list-property') : toast.error('Please verify your PAN first to add a property')}
                      className="text-[#7CB342] disabled:opacity-60"
                      disabled={!panVerified}
                      title={!panVerified ? 'Verify your PAN first' : ''}
                    >
                      Add your first property
                    </Button>
                  </div>
                ) : (
                  properties.map((property) => (
                    <div
                      key={property.id}
                      onClick={() => property.status === 'approved' && setSelectedProperty(property)}
                      className={`p-3 rounded-lg border transition-all ${
                        selectedProperty?.id === property.id 
                          ? 'border-[#7CB342] bg-green-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      } ${property.status === 'approved' ? 'cursor-pointer' : 'opacity-60'}`}
                    >
                      <div className="flex gap-3">
                        <img 
                          src={resolveImageUrl(property.images?.[0])} 
                          alt={property.title}
                          className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400'; }}
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-sm truncate">{property.title}</h3>
                          <p className="text-xs text-gray-500 truncate">{property.location}</p>
                          <div className="mt-1 flex items-center gap-2 flex-wrap">
                            {getStatusBadge(property.status)}
                            {property.pending_edit?.status === 'pending' && (
                              <span
                                data-testid="host-pending-edit-badge"
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700 border border-blue-200"
                                title={`Edit submitted on ${new Date(property.pending_edit.submitted_at).toLocaleDateString()} - awaiting admin review`}
                              >
                                Edit Pending Review
                              </span>
                            )}
                            {property.last_rejected_edit && !property.pending_edit && (
                              <span
                                data-testid="host-rejected-edit-badge"
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700 border border-red-200"
                                title={property.last_rejected_edit.rejection_reason || 'Edit rejected by admin'}
                              >
                                Last Edit Rejected
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {property.status === 'approved' && (
                        <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                          <span>₹{property.price?.toLocaleString()}/night</span>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/property/${property.id}/edit`);
                              }}
                              className="h-6 px-2 text-xs"
                            >
                              Edit
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/property/${property.slug || property.id}`);
                              }}
                              className="h-6 px-2 text-xs"
                            >
                              <Eye className="h-3 w-3 mr-1" /> View
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Calendar Section */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CalendarIcon className="h-5 w-5 text-[#7CB342]" />
                    Availability Calendar
                  </CardTitle>
                  {selectedProperty && (
                    <span className="text-sm text-gray-500">{selectedProperty.title}</span>
                  )}
                </div>
                {selectedProperty?.listing_type === 'rooms' && selectedProperty?.rooms?.length > 0 && (
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-600">Room:</span>
                    <select
                      value={selectedRoomId}
                      onChange={(e) => setSelectedRoomId(e.target.value)}
                      className="text-sm border rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-[#7CB342]/30"
                      data-testid="calendar-room-filter"
                    >
                      <option value="all">All Rooms</option>
                      {selectedProperty.rooms.map((r) => (
                        <option key={r.room_id} value={r.room_id}>
                          {r.category} (inv: {r.inventory})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {!selectedProperty ? (
                  <div className="text-center py-12 text-gray-500">
                    <CalendarIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <p className="font-medium">Select an approved property</p>
                    <p className="text-sm">to manage its availability calendar</p>
                  </div>
                ) : (
                  <>
                    {/* Month Navigation */}
                    <div className="flex items-center justify-between mb-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (calendarMonth === 0) {
                            setCalendarMonth(11);
                            setCalendarYear(calendarYear - 1);
                          } else {
                            setCalendarMonth(calendarMonth - 1);
                          }
                        }}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <h3 className="text-lg font-semibold">
                        {MONTHS[calendarMonth]} {calendarYear}
                      </h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (calendarMonth === 11) {
                            setCalendarMonth(0);
                            setCalendarYear(calendarYear + 1);
                          } else {
                            setCalendarMonth(calendarMonth + 1);
                          }
                        }}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Legend */}
                    <div className="flex flex-wrap gap-4 mb-4 text-xs">
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 bg-white border rounded" />
                        <span>Available</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 bg-gray-300 rounded" />
                        <span>Blocked</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 bg-red-100 rounded" />
                        <span>Booked</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 bg-[#7CB342] rounded" />
                        <span>Selected</span>
                      </div>
                    </div>

                    {/* Calendar Grid */}
                    {calendarLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-[#7CB342]" />
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-7 gap-1 mb-2">
                          {DAYS.map(day => (
                            <div key={day} className="h-8 flex items-center justify-center text-xs font-medium text-gray-500">
                              {day}
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-7 gap-1">
                          {renderCalendar()}
                        </div>
                      </>
                    )}

                    {/* Action Buttons */}
                    <div className="mt-6 flex flex-col sm:flex-row gap-3">
                      <div className="flex-1 text-sm text-gray-500">
                        {selectedDates.length > 0 ? (
                          <span className="font-medium text-gray-900">
                            {selectedDates.length} date(s) selected
                          </span>
                        ) : (
                          <span>Click on dates to select them</span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleBlockDates}
                          disabled={selectedDates.length === 0 || actionLoading}
                          variant="outline"
                          className="flex-1 sm:flex-none"
                        >
                          {actionLoading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Lock className="h-4 w-4 mr-2" />
                          )}
                          Block Dates
                        </Button>
                        <Button
                          onClick={handleUnblockDates}
                          disabled={selectedDates.length === 0 || actionLoading}
                          className="flex-1 sm:flex-none bg-[#7CB342] hover:bg-[#6da036] text-white"
                        >
                          {actionLoading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Unlock className="h-4 w-4 mr-2" />
                          )}
                          Unblock Dates
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Guest Bookings Section */}
        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-[#7CB342]" />
                Guest Bookings
              </CardTitle>
            </CardHeader>
            <CardContent>
              {bookingsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-[#7CB342]" />
                </div>
              ) : hostBookings.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-14 w-14 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No bookings yet</p>
                  <p className="text-sm">Bookings from guests will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {hostBookings.map((booking) => (
                    <div
                      key={booking.id || booking._id}
                      className="border rounded-lg p-4 hover:shadow-sm transition-shadow"
                      data-testid="host-booking-card"
                    >
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-semibold text-gray-900">{booking.property_title}</h4>
                              <p className="text-sm text-gray-500 flex items-center mt-0.5">
                                <MapPin className="h-3.5 w-3.5 mr-1" />
                                {booking.property_location}
                              </p>
                            </div>
                            <Badge className={
                              booking.booking_status === 'confirmed' ? 'bg-green-100 text-green-700' :
                              booking.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                              'bg-yellow-100 text-yellow-700'
                            }>
                              {booking.booking_status === 'confirmed' || booking.payment_status === 'paid' ? 'Confirmed' : 'Pending'}
                            </Badge>
                          </div>

                          {/* Booking Details */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-3">
                            <div>
                              <p className="text-gray-500">Check-in</p>
                              <p className="font-medium">{booking.check_in}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Check-out</p>
                              <p className="font-medium">{booking.check_out}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Nights</p>
                              <p className="font-medium">{booking.nights}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Guest Paid</p>
                              <p className="font-semibold">₹{(booking.total_amount || 0).toLocaleString()}</p>
                            </div>
                          </div>

                          {/* Payout Breakdown (host's view) */}
                          <div className="bg-[#7CB342]/5 border border-[#7CB342]/20 rounded-lg p-3 mb-3" data-testid="host-payout-breakdown">
                            <div className="flex items-center justify-between text-xs text-gray-600">
                              <span>Booking subtotal</span>
                              <span>₹{((booking.subtotal ?? booking.total_amount ?? 0)).toLocaleString()}</span>
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-600 mt-1">
                              <span>− Platform fee ({Math.round(((booking.platform_fee_rate ?? 0.10)) * 100)}%)</span>
                              <span className="text-red-600">−₹{((booking.platform_fee ?? Math.round((booking.subtotal ?? booking.total_amount ?? 0) * 0.10 * 100) / 100)).toLocaleString()}</span>
                            </div>
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#7CB342]/20">
                              <span className="text-sm font-semibold text-gray-700">Your net payout</span>
                              <span className="text-base font-bold text-[#7CB342]" data-testid="host-net-payout">
                                ₹{(
                                  (booking.subtotal ?? booking.total_amount ?? 0) -
                                  (booking.platform_fee ?? Math.round((booking.subtotal ?? booking.total_amount ?? 0) * 0.10 * 100) / 100)
                                ).toLocaleString()}
                              </span>
                            </div>
                          </div>

                          {/* Guest Contact Details */}
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Guest Contact Details</p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                <span className="font-medium">{booking.guest_name || 'N/A'}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                <a href={`mailto:${booking.guest_email}`} className="text-[#7CB342] hover:underline truncate">
                                  {booking.guest_email || 'N/A'}
                                </a>
                              </div>
                              <div className="flex items-center gap-2">
                                <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                <a href={`tel:${booking.guest_phone}`} className="text-[#7CB342] hover:underline">
                                  {booking.guest_phone || 'N/A'}
                                </a>
                              </div>
                            </div>
                            {booking.special_requests && (
                              <div className="mt-2 text-sm">
                                <span className="text-gray-500">Special Requests: </span>
                                <span className="text-gray-700">{booking.special_requests}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Cancellation Requests Section */}
        <div className="mt-8" data-testid="host-cancellations-section">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                Cancellation Requests
                {cancellations.filter(c => c.status === 'pending').length > 0 && (
                  <Badge className="bg-orange-100 text-orange-700 ml-2">
                    {cancellations.filter(c => c.status === 'pending').length} pending
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {cancellationsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-[#7CB342]" />
                </div>
              ) : cancellations.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <AlertTriangle className="h-14 w-14 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No cancellation requests</p>
                  <p className="text-sm">Guest cancellation requests will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cancellations.map((cancel) => {
                    const isPending = cancel.status === 'pending';
                    const statusBadge = cancel.status === 'accepted'
                      ? 'bg-green-100 text-green-700'
                      : cancel.status === 'rejected'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-orange-100 text-orange-700';
                    const isActive = respondingId === cancel.cancellation_id;
                    return (
                      <div
                        key={cancel.cancellation_id}
                        className={`border rounded-lg p-4 ${isPending ? 'border-orange-300 bg-orange-50/40' : ''}`}
                        data-testid={`cancellation-card-${cancel.cancellation_id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-semibold text-gray-900">{cancel.property_title}</h4>
                            <p className="text-sm text-gray-500">
                              {cancel.check_in} to {cancel.check_out}
                            </p>
                          </div>
                          <Badge className={statusBadge}>
                            {cancel.status.charAt(0).toUpperCase() + cancel.status.slice(1)}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-3">
                          <div className="bg-gray-50 rounded-md p-2">
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Guest</p>
                            <p className="font-medium">{cancel.guest_name}</p>
                            <p className="text-xs text-gray-500">{cancel.guest_email}</p>
                          </div>
                          <div className="bg-gray-50 rounded-md p-2">
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Amount</p>
                            <p className="font-semibold text-[#7CB342]">
                              ₹{(cancel.total_amount || 0).toLocaleString()}
                            </p>
                          </div>
                        </div>

                        <div className="bg-white border rounded-md p-3 mb-3">
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Guest's reason</p>
                          <p className="text-sm text-gray-800">{cancel.reason}</p>
                        </div>

                        {cancel.host_message && !isPending && (
                          <div className="bg-gray-50 rounded-md p-3 mb-3">
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Your response</p>
                            <p className="text-sm text-gray-800">{cancel.host_message}</p>
                          </div>
                        )}

                        {isPending && (
                          isActive ? (
                            <div className="space-y-3 bg-white border rounded-md p-3">
                              <p className="text-sm font-medium">
                                {respondAction === 'accept'
                                  ? 'Accept cancellation — calendar dates will be freed and guest will be notified'
                                  : 'Reject cancellation — booking will remain confirmed'}
                              </p>
                              <Textarea
                                value={hostMessage}
                                onChange={(e) => setHostMessage(e.target.value)}
                                placeholder="Optional message to the guest..."
                                rows={2}
                                data-testid="host-cancel-message-input"
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleCancellationResponse(cancel.cancellation_id, respondAction)}
                                  disabled={responding}
                                  className={respondAction === 'accept'
                                    ? 'bg-[#7CB342] hover:bg-[#6da036] text-white'
                                    : 'bg-red-600 hover:bg-red-700 text-white'}
                                  data-testid="confirm-cancel-response-btn"
                                >
                                  {responding ? (
                                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                  ) : respondAction === 'accept' ? (
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                  ) : (
                                    <XCircle className="h-3 w-3 mr-1" />
                                  )}
                                  Confirm {respondAction === 'accept' ? 'Accept' : 'Reject'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => { setRespondingId(null); setRespondAction(null); setHostMessage(''); }}
                                >
                                  Back
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => { setRespondingId(cancel.cancellation_id); setRespondAction('accept'); setHostMessage(''); }}
                                className="bg-[#7CB342] hover:bg-[#6da036] text-white"
                                data-testid={`accept-cancel-btn-${cancel.cancellation_id}`}
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" /> Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => { setRespondingId(cancel.cancellation_id); setRespondAction('reject'); setHostMessage(''); }}
                                data-testid={`reject-cancel-btn-${cancel.cancellation_id}`}
                              >
                                <XCircle className="h-3 w-3 mr-1" /> Reject
                              </Button>
                            </div>
                          )
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HostDashboardPage;
